import {z} from 'zod';

import {getCourseDocumentPath} from './course';

// ============================================================================
// Path segments
// ============================================================================

export const SECTION_SCHEDULE_SUBCOLLECTION = 'sectionSchedule' as const;
export const SECTION_SCHEDULE_DOCUMENT_ID = 'schedule' as const;

// ============================================================================
// Schema
// ============================================================================

/** What a week's row on the section page links to. */
export const SECTION_MATERIALS_KINDS = ['handoutAndCode', 'slides'] as const;

/** Zod schema for {@link SectionMaterialsKind}. */
export const SectionMaterialsKindSchema = z.enum(SECTION_MATERIALS_KINDS);

/**
 * `handoutAndCode`: the week's handout page and its problem in the IDE.
 * `slides`: the deck staff picked for the week on its section guide.
 */
export type SectionMaterialsKind = z.infer<typeof SectionMaterialsKindSchema>;

/** The kind a schedule without a `materials` field uses. */
export const DEFAULT_SECTION_MATERIALS_KIND: SectionMaterialsKind =
  'handoutAndCode';

/** One question of a week's catch-up check. */
export const CatchUpCheckQuestionSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('choice'),
    /** The question, shown as written. */
    prompt: z.string().trim().min(1),
    /** The choices, in order; at least two. */
    options: z.array(z.string().trim().min(1)).min(2),
    /** The zero-based index of the correct choice. */
    answer: z.number().int().nonnegative(),
  }),
  z.object({
    kind: z.literal('number'),
    /** The question, shown as written. */
    prompt: z.string().trim().min(1),
    /** The correct number. */
    answer: z.number(),
    /**
     * How far from `answer` a response may be and still count (the closed
     * interval `[answer - tolerance, answer + tolerance]`). Omitted: exact.
     */
    tolerance: z.number().nonnegative().optional(),
  }),
]);

/** One question of a week's catch-up check. */
export type CatchUpCheckQuestion = z.infer<typeof CatchUpCheckQuestionSchema>;

/**
 * A week's catch-up check: a few short questions on the section's problem,
 * graded by the attendance callable. Answers live here with the questions;
 * the check is a short exercise with unlimited retries, not an exam.
 */
export const CatchUpCheckSchema = z.object({
  questions: z.array(CatchUpCheckQuestionSchema).min(1),
});

/** A week's catch-up check. */
export type CatchUpCheck = z.infer<typeof CatchUpCheckSchema>;

/** The kinds of catch-up exercise a week can use. */
export const CATCH_UP_EXERCISE_KINDS = ['teachBack', 'check'] as const;

/** Zod schema for {@link CatchUpExerciseKind}. */
export const CatchUpExerciseKindSchema = z.enum(CATCH_UP_EXERCISE_KINDS);

/**
 * `teachBack`: the week's SimTeach puzzle (`teachBackByWeek`). `check`: the
 * week's catch-up check (`catchUpCheckByWeek`).
 */
export type CatchUpExerciseKind = z.infer<typeof CatchUpExerciseKindSchema>;

export const SectionScheduleDocumentSchema = z
  .object({
    /** One topic per section week in order; the first entry is week 1. */
    topics: z.array(z.string()).optional(),
    /**
     * What each week's row links to. Omitted means
     * {@link DEFAULT_SECTION_MATERIALS_KIND}.
     */
    materials: SectionMaterialsKindSchema.optional(),
    /**
     * Each section week's teach-back, aligned with `topics`: the SimTeach
     * puzzle a student completes so a week that rests on a recording, or on
     * an unverified Super Section join, counts. A `null` or missing entry
     * means the week has none. See `@cip/core/section` `sectionWeekCredit.ts`.
     */
    teachBackByWeek: z
      .array(
        z
          .object({
            /** The puzzle's id in `course/{courseId}/simTeachPuzzles`. */
            puzzleId: z.string().min(1),
          })
          .nullable(),
      )
      .optional(),
    /**
     * Each section week's catch-up check, aligned with `topics`; `null` or
     * missing means the week has none authored. Authoring one does not show
     * it: `catchUpExerciseByWeek` picks what students get.
     */
    catchUpCheckByWeek: z.array(CatchUpCheckSchema.nullable()).optional(),
    /**
     * Which exercise each section week uses, aligned with `topics`; `null`
     * means none. When the field is missing, a week uses its teach-back if
     * `teachBackByWeek` sets one, so a schedule written before this field
     * keeps working. See `getCatchUpExercise` in `sectionWeekCredit.ts`.
     */
    catchUpExerciseByWeek: z
      .array(CatchUpExerciseKindSchema.nullable())
      .optional(),
  })
  .loose();

export type SectionScheduleDocument = z.infer<
  typeof SectionScheduleDocumentSchema
>;

// ============================================================================
// Paths
// ============================================================================

export function getSectionScheduleDocumentPath({
  courseId,
}: {
  courseId: string;
}): string {
  return `${getCourseDocumentPath({courseId})}/${SECTION_SCHEDULE_SUBCOLLECTION}/${SECTION_SCHEDULE_DOCUMENT_ID}`;
}
